/**
 * `/login` (spec M0-F3 §8.2), laid out from the capture
 * `discovery/ui-reference/first-look/login-form.{png,json}`: an H1, the account name as the
 * subtitle, the labels "Your email address" and "Enter password", email → password on one
 * screen, "Forgot your password?" above the primary button, and a "Join account" affordance
 * that only appears when the account allows domain self-join.
 *
 * One error style only: an inline red line reading **"Wrong email or password."** for every
 * failure mode — unknown email, wrong password, pending, inactive, deleted, locked. No
 * enumeration, no timing difference (§5.4 step 3).
 *
 * M0-F3b §8.1 adds the **"Sign in with Eli Z Platform"** button. monday puts its federated
 * buttons in a row **directly under the primary button** — the blue `Log in` is 404 × 48 at
 * y 418 and `Google` / `Microsoft` / `Email Code` sit at y 490, each 40 px tall with a 4 px
 * radius, a 1 px border and ink `rgb(50,51,56)` (`login-form.json`, measured). Ours takes that
 * slot with one full-width button. It is a plain link, not a fetch: the flow is a browser
 * navigation and has to work before any JavaScript has settled.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import { AuthError, AuthShell } from './AuthShell';
import { safeNextPath } from './next-path';
import { looksLikeServerInternals } from '../../app/errors';

const WRONG_CREDENTIALS = 'Wrong email or password.';

export function Login() {
  const trpc = useTRPC();
  const client = useTRPCClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('next'));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const info = useQuery(trpc.system.authInfo.queryOptions());

  // Already authenticated ⇒ straight to the `?next=` target (§8.2).
  useEffect(() => {
    if (info.data?.authenticated) void navigate(next, { replace: true });
  }, [info.data?.authenticated, navigate, next]);

  const login = useMutation({
    mutationFn: (input: { email: string; password: string }) => client.auth.login.mutate(input),
    onSuccess: (result) => {
      if (result.status === 'verification_required') {
        void navigate(`/login/verify?challenge=${encodeURIComponent(result.challengeId)}`, {
          state: { next },
        });
        return;
      }
      window.location.assign(next);
    },
    onError: (caught: unknown) => {
      const shape = caught as { data?: { code?: string }; message?: string };
      /* P-049: a throttle message is the api's sentence; anything that looks like a
         statement, a parameter list or a stack is never printed on the login page. */
      const throttled = shape.data?.code === 'TOO_MANY_REQUESTS';
      const message = shape.message?.trim() ?? '';
      setError(
        throttled
          ? message !== '' && !looksLikeServerInternals(message)
            ? message
            : 'Too many attempts. Try again later.'
          : WRONG_CREDENTIALS,
      );
    },
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (login.isPending || email.trim() === '' || password === '') return;
    setError(null);
    login.mutate({ email: email.trim(), password });
  };

  const accountName = info.data?.accountName ?? 'Eli Z Monday';
  const selfJoinDomain = info.data?.selfJoinDomains?.[0];
  const sso = info.data?.sso;
  const ssoEnabled = sso?.enabled === true;
  // Defensive: only an explicit `false` from a server that also offers SSO hides the password
  // form. An older api, a failed query or an undefined field must never lock everyone out.
  const passwordLogin = !ssoEnabled || sso?.allowPasswordLogin !== false;

  const ssoButton = ssoEnabled ? (
    <a
      className="ez-auth__sso"
      href={`/auth/oidc/start?next=${encodeURIComponent(next)}`}
      data-testid="login-sso"
    >
      {sso?.label ?? 'Sign in with Eli Z Platform'}
    </a>
  ) : null;

  if (!passwordLogin) {
    return (
      <AuthShell
        title="Log in"
        subtitle={`${accountName} — sign in with your Eli Z Platform account`}
        testId="login-page"
      >
        {ssoButton}
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Log in" subtitle={accountName} testId="login-page">
      <form className="ez-auth__form" onSubmit={submit} noValidate>
        <div>
          <label className="ez-auth__label" htmlFor="login-email">
            Your email address
          </label>
          <div className="ez-auth__field">
            <input
              id="login-email"
              className="ez-auth__input"
              type="email"
              name="email"
              autoComplete="username"
              placeholder="Email"
              // The email field is the first thing a person types on this page (§8.2).
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              data-testid="login-email"
            />
          </div>
        </div>

        <div>
          <label className="ez-auth__label" htmlFor="login-password">
            Enter password
          </label>
          <div className="ez-auth__field">
            <input
              id="login-password"
              className="ez-auth__input"
              type={revealed ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              data-testid="login-password"
            />
            <button
              type="button"
              className="ez-auth__reveal"
              onClick={() => setRevealed((value) => !value)}
              aria-label={revealed ? 'Hide password' : 'Show password'}
              data-testid="login-reveal"
            >
              {revealed ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <AuthError>{error}</AuthError>

        <p className="ez-auth__links" style={{ marginTop: 0, textAlign: 'left' }}>
          <a className="ez-auth__link" href="/forgot-password" data-testid="login-forgot">
            Forgot your password?
          </a>
        </p>

        <button
          type="submit"
          className="ez-auth__button"
          disabled={login.isPending || email.trim() === '' || password === ''}
          data-testid="login-submit"
        >
          {login.isPending ? 'Logging in…' : 'Log in'}
        </button>
      </form>

      {ssoButton}

      {selfJoinDomain === undefined ? null : (
        <p className="ez-auth__links">
          <a className="ez-auth__link" href="/join" data-testid="login-join">
            {`Join ${accountName} with your @${selfJoinDomain} email`}
          </a>
        </p>
      )}
    </AuthShell>
  );
}
